import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw,
  BarChart3,
  CheckCircle2,
  DollarSign,
  XCircle,
  Clock,
  Download,
} from "lucide-react";

const STATUS_BARS = [
  { key: "submitted", label: "Received", color: "bg-amber-400" },
  { key: "under_review", label: "Under Review", color: "bg-sky-400" },
  { key: "approved", label: "Approved", color: "bg-emerald-400" },
  { key: "paid", label: "Paid", color: "bg-violet-400" },
  { key: "rejected", label: "Rejected", color: "bg-rose-400" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });
  } catch {
    return "";
  }
}

// Pretty date used in the daily table — "Thu, May 21". `iso` is "YYYY-MM-DD".
function fmtDayLabel(iso) {
  if (!iso) return "—";
  // Build at UTC noon to dodge any DST/offset edge cases that could roll
  // the rendered weekday into the previous day.
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/analytics/summary", { params: { days: 30 } });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportDay = useCallback(async (date) => {
    try {
      const r = await api.get("/admin/submissions/export.csv", {
        params: { date_from: date, date_to: date },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gcprimo-submissions-${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${date}`);
    } catch {
      toast.error("Export failed");
    }
  }, []);

  const exportCurrentMonth = useCallback(async () => {
    const now = new Date();
    const yyyy = now.getUTCFullYear();
    const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
    const from = `${yyyy}-${mm}-01`;
    const lastDay = new Date(Date.UTC(yyyy, now.getUTCMonth() + 1, 0)).getUTCDate();
    const to = `${yyyy}-${mm}-${String(lastDay).padStart(2, "0")}`;
    try {
      const r = await api.get("/admin/submissions/export.csv", {
        params: { date_from: from, date_to: to, limit: 50000 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gcprimo-submissions-${yyyy}-${mm}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${now.toLocaleString("en-US", { month: "long", year: "numeric" })}`);
    } catch {
      toast.error("Export failed");
    }
  }, []);

  if (!data) {
    return (
      <div className="p-6 md:p-8 text-slate-400" data-testid="admin-analytics">
        {loading ? "Loading analytics…" : "No analytics data."}
      </div>
    );
  }

  const total = data.total || 0;
  const approved = data.counts?.approved ?? 0;
  const paid = data.counts?.paid ?? 0;
  const rejected = data.counts?.rejected ?? 0;

  return (
    <div className="px-4 md:px-6 py-6 space-y-6" data-testid="admin-analytics">
      {/* Dashboard Analytics card */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Dashboard Analytics</h2>
            <p className="text-sm text-slate-500 mt-1">Overview of all submissions and key metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Updated {fmtTime(data.generated_at)}</span>
            <button
              onClick={load}
              disabled={loading}
              data-testid="analytics-refresh"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={async () => {
                try {
                  const r = await api.get("/admin/analytics/export.csv", {
                    params: { days: 30 },
                    responseType: "blob",
                  });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `gcprimo-analytics-30d-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("CSV downloaded");
                } catch {
                  toast.error("Export failed");
                }
              }}
              data-testid="analytics-export-csv"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Big stat tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatTile label="TOTAL" value={total} accent="text-sky-600" bg="bg-sky-50" Icon={BarChart3} iconBg="bg-sky-100 text-sky-600" />
          <StatTile label="APPROVED" value={approved} accent="text-emerald-600" bg="bg-emerald-50" Icon={CheckCircle2} iconBg="bg-emerald-100 text-emerald-600" />
          <StatTile label="PAID" value={paid} accent="text-violet-600" bg="bg-violet-50" Icon={DollarSign} iconBg="bg-violet-100 text-violet-600" />
          <StatTile label="REJECTED" value={rejected} accent="text-rose-600" bg="bg-rose-50" Icon={XCircle} iconBg="bg-rose-100 text-rose-600" />
        </div>

        {/* Status Breakdown bars */}
        <div>
          <h3 className="text-base font-bold text-slate-900 mb-4">Status Breakdown</h3>
          <div className="space-y-3" data-testid="status-breakdown">
            {STATUS_BARS.map((s) => {
              const count = data.counts?.[s.key] ?? 0;
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={s.key} className="grid grid-cols-[140px_1fr_60px_50px] items-center gap-3 text-sm">
                  <div className="text-slate-600">{s.label}</div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`${s.color} h-full rounded-full transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-right font-bold text-slate-900 font-mono">{count}</div>
                  <div className="text-right text-xs text-slate-400">{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Top Card Brands */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
        <h3 className="text-sm font-bold tracking-wider text-slate-900 mb-5">TOP CARD BRANDS</h3>
        {data.top_brands.length === 0 ? (
          <p className="text-sm text-slate-400">No brand data yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.top_brands.map((b, i) => (
              <li key={`${b.brand}-${i}`} className="flex items-center gap-4 py-3" data-testid={`brand-row-${i}`}>
                <span className="w-9 h-9 rounded-full border-2 border-sky-200 bg-sky-50 text-sky-600 flex items-center justify-center text-sm font-bold">
                  {i + 1}
                </span>
                <span className="flex-1 text-slate-900 font-medium">{b.brand}</span>
                <span className="px-3 py-1 rounded-lg bg-slate-100 text-slate-900 font-bold text-sm font-mono">
                  {b.count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Daily Submissions */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Daily Submissions</h3>
            <p className="text-sm text-slate-500 mt-1">Track how many cards are submitted each day</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCurrentMonth}
              data-testid="daily-export-month"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export current month
            </button>
            <button
              onClick={load}
              disabled={loading}
              data-testid="daily-refresh"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Daily totals row */}
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3 mb-6">
          <DailyTile label="Total" value={data.daily_totals.total} cls="text-slate-900" />
          <DailyTile label="Unique" value={data.daily_totals.unique} cls="text-sky-600" />
          <DailyTile label="Dupes" value={data.daily_totals.dupes} cls="text-orange-500" />
          <DailyTile label="Approved" value={data.daily_totals.approved} cls="text-emerald-600" />
          <DailyTile label="Paid" value={data.daily_totals.paid} cls="text-violet-600" />
          <DailyTile label="Pending" value={data.daily_totals.pending} cls="text-amber-500" />
          <DailyTile label="Rejected" value={data.daily_totals.rejected} cls="text-rose-500" />
          <DailyTile label="Value" value={`$${data.daily_totals.value.toLocaleString()}`} cls="text-emerald-700" />
        </div>

        {/* Daily table */}
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Date</th>
                <th className="text-right px-5 py-3 font-semibold text-sky-600">Total</th>
                <th className="text-right px-5 py-3 font-semibold text-sky-500">Unique</th>
                <th className="text-right px-5 py-3 font-semibold text-orange-500">Dupes</th>
                <th className="text-right px-5 py-3 font-semibold text-emerald-600">Approved</th>
                <th className="text-right px-5 py-3 font-semibold text-violet-600">Paid</th>
                <th className="text-right px-5 py-3 font-semibold text-amber-500">Pending</th>
                <th className="text-right px-5 py-3 font-semibold text-rose-500">Rejected</th>
                <th className="text-right px-5 py-3 font-semibold">Value</th>
                <th className="text-center px-5 py-3 font-semibold">Export</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.map((d) => (
                <tr key={d.date} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`daily-row-${d.date}`}>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{fmtDayLabel(d.date)}</td>
                  <td className="px-5 py-3 text-right">
                    <span className="inline-flex items-center justify-center min-w-[36px] h-8 px-2 rounded-full bg-sky-100 text-sky-700 font-bold">{d.total}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="inline-flex items-center justify-center min-w-[36px] h-8 px-2 rounded-full bg-sky-50 text-sky-600 font-semibold">{d.unique}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="inline-flex items-center justify-center min-w-[36px] h-8 px-2 rounded-full bg-orange-50 text-orange-500 font-semibold">{d.dupes}</span>
                  </td>
                  <td className="px-5 py-3 text-right text-emerald-600 font-semibold">{d.approved}</td>
                  <td className="px-5 py-3 text-right text-violet-600 font-semibold">{d.paid}</td>
                  <td className="px-5 py-3 text-right">
                    <span className="inline-flex items-center justify-center min-w-[36px] h-8 px-2 rounded-full bg-amber-50 text-amber-500 font-semibold">{d.pending}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="inline-flex items-center justify-center min-w-[36px] h-8 px-2 rounded-full bg-rose-50 text-rose-500 font-semibold">{d.rejected}</span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-slate-900">${d.value.toLocaleString()}</td>
                  <td className="px-5 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => exportDay(d.date)}
                      title={`Export ${fmtDayLabel(d.date)} as CSV`}
                      data-testid={`daily-export-${d.date}`}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {data.daily.length === 0 && (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-sm text-slate-400">No submissions in the last 30 days.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatTile({ label, value, accent, bg, Icon, iconBg }) {
  return (
    <div className={`${bg} rounded-2xl p-5 border border-slate-100`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</span>
      </div>
      <div className={`mt-3 text-4xl font-bold ${accent} font-mono`}>{value}</div>
    </div>
  );
}

function DailyTile({ label, value, cls }) {
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-3 text-center">
      <div className={`text-xl font-bold ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

// reuse icons silently
void Clock;
